(function () {
    'use strict';

    const WATCHER_ENDPOINT = 'http://pulse.behope.com/api/logs';

    const CONFIG = {
        sameOriginOnly: true,
        errorStatusMinimum: 500,
        maxStringLength: 3000
    };

    const watcherConfig = window.WATCHER_CONFIG || {};

    function truncate(value, maxLength = CONFIG.maxStringLength) {
        if (value === undefined || value === null) {
            return '';
        }

        const stringValue = String(value);

        return stringValue.length <= maxLength
            ? stringValue
            : stringValue.substring(0, maxLength) + '...';
    }

    function getSafeUrl(url) {
        try {
            return new URL(url, window.location.origin).href;
        } catch {
            return String(url || '');
        }
    }

    function getSafePath(url) {
        try {
            return new URL(url, window.location.origin).pathname;
        } catch {
            return String(url || '').split('?')[0];
        }
    }

    function isSameOrigin(url) {
        try {
            return (
                new URL(url, window.location.origin).origin ===
                window.location.origin
            );
        } catch {
            return false;
        }
    }

    function shouldMonitorUrl(url) {
        if (!url) {
            return false;
        }

        if (
            CONFIG.sameOriginOnly &&
            !isSameOrigin(url)
        ) {
            return false;
        }

        return true;
    }

    function createWatcherEvent({
        severity = 'ERROR',
        path,
        title,
        details = {},
        stackTrace = ''
    }) {
        return {
            timestamp: new Date().toISOString(),
            severity,
            service: 'BeHope Storefront',
            path,
            title,
            details,
            stackTrace: truncate(stackTrace)
        };
    }

    function sendToWatcher(event) {
        try {
            fetch(WATCHER_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(event),
                keepalive: true
            }).catch(() => {});
        } catch {}
    }

    /* =========================================================
     * 1. Shopify 404 Page
     * ======================================================= */

    function monitor404Page() {
        if (watcherConfig.template !== '404') {
            return;
        }

        sendToWatcher(
            createWatcherEvent({
                severity: 'ERROR',
                path: window.location.href,
                title: 'Shopify Storefront 404',
                details: {
                    eventType: 'PAGE_NOT_FOUND',
                    statusCode: 404,
                    requestMethod: 'GET',
                    template: '404'
                }
            })
        );
    }


    /* =========================================================
     * 2. Add To Cart Detection
     * ======================================================= */

    function isAddToCartRequest(url) {
        if (!url) {
            return false;
        }

        const path = getSafePath(url);

        return (
            path === '/cart/add' ||
            path === '/cart/add.js' ||
            path.startsWith('/cart/add/')
        );
    }

    /* =========================================================
     * 3. Fetch Monitoring
     *
     * Monitors general storefront HTTP errors.
     * ======================================================= */

    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
        const request = args[0];
        const requestUrl = typeof request === 'string' ? request : request?.url;

        const requestOptions = args[1] || {};

        const method = ( requestOptions.method || request?.method || 'GET' ).toUpperCase();

        if (!shouldMonitorUrl(requestUrl)) {
            return originalFetch.apply(this, args);
        }

        const startTime = performance.now();

        try {
            const response = await originalFetch.apply(this, args);

            const duration = Math.round(
                performance.now() - startTime
            );

            if ( response.status >= CONFIG.errorStatusMinimum ) {
                // Add to Cart Errors handled separately
                if (isAddToCartRequest(requestUrl)) {
                    return response;
                }

                sendToWatcher(
                    createWatcherEvent({
                        severity: response.status >= 500 ? 'CRITICAL' : 'ERROR',
                        path: getSafeUrl( response.url || requestUrl ),
                        title: 'Shopify Storefront HTTP Error',
                        details: {
                            eventType: 'HTTP_ERROR',
                            requestMethod: method,
                            statusCode: response.status,
                            statusText: truncate( response.statusText ),
                            page: window.location.href,
                            durationMs: duration
                        }
                    })
                );
            }

            return response;
        } catch (error) {
            throw error;
        }
    };

    /* =========================================================
     * 4. XMLHttpRequest Monitoring
     * ======================================================= */

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (
        method,
        url,
        ...rest
    ) {
        this.__watcherMethod = method;
        this.__watcherUrl = url;

        return originalOpen.call(
            this,
            method,
            url,
            ...rest
        );
    };

    XMLHttpRequest.prototype.send = function (...args) {
        const xhr = this;
        const requestUrl = xhr.__watcherUrl;

        if (!shouldMonitorUrl(requestUrl)) {
            return originalSend.apply(this, args);
        }

        const startTime = performance.now();

        xhr.addEventListener('loadend', function () {
            const duration = Math.round(
                performance.now() - startTime
            );

            if ( xhr.status < CONFIG.errorStatusMinimum ) {
                return;
            }

            if (isAddToCartRequest(requestUrl)) {
                return;
            }

            sendToWatcher(
                createWatcherEvent({
                    severity: xhr.status >= 500 ? 'CRITICAL' : 'ERROR',
                    path: getSafeUrl(requestUrl),
                    title: 'Shopify Storefront HTTP Error',
                    details: {
                        eventType: 'HTTP_ERROR',
                        requestMethod: String( xhr.__watcherMethod || 'GET').toUpperCase(),
                        statusCode: xhr.status,
                        page: window.location.href,
                        durationMs: duration
                    }
                })
            );
        });

        return originalSend.apply(this, args);
    };

    /* =========================================================
     * 5. Add To Cart Application Errors
     * ======================================================= */

    document.addEventListener(
        'on:cart:error',
        function (event) {
            const errorMessage =
                event.detail?.error;

            if (!errorMessage) {
                return;
            }

            sendToWatcher(
                createWatcherEvent({
                    severity: 'ERROR',
                    path: window.location.href,
                    title: 'Add To Cart Failed',
                    details: {
                        eventType: 'ADD_TO_CART_ERROR',
                        reason: 'Shopify rejected the add-to-cart request',
                        error: truncate(errorMessage),
                        page: window.location.href
                    }
                })
            );
        }
    );


    /* =========================================================
     * 6. Initialize
     * ======================================================= */

    function initialize() {
        monitor404Page();
    }

    if (document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            initialize
        );
    } else {
        initialize();
    }
})();